import { type LayoutGroupData } from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { spacing } from '@/features/shared/theme/tokens'
import { type Size } from '@shipt/design-system-themes'
import styled from 'styled-components'
import { loadBeaconsFromCMSContent } from '@/features/cms/components/CMS/utils'
import { getComponentForContentTypeId } from '@/features/cms/components/CMS/componentUtils'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Column } from '@shipt/design-system-layouts'
import { useEffect } from 'react'
import { DeferredShelf } from '@/features/cms/components/CMS/DeferredShelf'

/**
 * This component corresponds to the `layout_group` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/layout_group
 * - **Production** - https://cms.shipt.com/content-type/pool/layout_group
 */
export const LayoutGroup = ({ content_type_id, id, data: parentData }: LayoutGroupData) => {
  useEffect(() => {
    if (parentData?.children) loadBeaconsFromCMSContent(parentData.children)
  }, [parentData?.children])
  if (!parentData) return
  const { children, vertical_spacing } = parentData
  if (!children?.length) return null

  try {
    return (
      <LayoutGroupContainer
        verticalSpacing={vertical_spacing}
        {...getCMSWrapperProps({ content_type_id, id })}
      >
        {children.map((data, idx) => {
          const { content_type_id: child_content_type_id, id: child_id } = data
          const Component = getComponentForContentTypeId({
            contentTypeId: child_content_type_id,
          })
          if (!Component) return null

          return (
            // It's possible for the same content to be used multiple times
            // within a layout group, so we can't rely on the id alone.
            // oxlint-disable-next-line react/no-array-index-key
            <DeferredShelf data={data} index={idx} key={`${child_id}-${idx}`}>
              {(resolvedData) => <Component {...resolvedData} index={idx} />}
            </DeferredShelf>
          )
        })}
      </LayoutGroupContainer>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const LayoutGroupContainer = styled(Column)<{
  verticalSpacing?: Size | 'none'
}>`
  gap: ${({ verticalSpacing }) =>
    verticalSpacing === 'none' ? 0 : spacing(verticalSpacing ?? 120)};
`
